import { transformEnumToDisplay } from '@fruitful/fruitils';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableExpandedRows,
    type DataTableSelectEvent,
    type DataTableValueArray,
} from 'primereact/datatable';
import React, { useEffect, useState } from 'react';
import {
    BadgeLabel,
    type BadgeLabelVariant,
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    H6Emphasized,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    type IdentityMatchUser,
    type PlaidAddress,
} from '../../banking/banking/types/ExternalAccount';
import { ConfirmModal } from '../../common/components/ConfirmModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    type UnverifiedExternalAccountResponse,
    useGetUnverifiedExternalAccounts,
    useUpdateExternalAccount,
} from '../hooks/useGetUnverifiedExternalAccounts';

const PlaidAddressWidget = ({ address }: { address: PlaidAddress }) => {
    const { city, region, postal_code, street, country } = address.data;
    return (
        <YStack padding={16} alignItems="flex-start">
            <BodyMediumRegular>{street}</BodyMediumRegular>
            <BodyMediumRegular>
                {city}, {region} {postal_code}
            </BodyMediumRegular>
            <BodyMediumRegular>{country}</BodyMediumRegular>
            <BodyMediumRegular>
                <BodySmallRegular>
                    {address.primary ? '(primary)' : ''}
                </BodySmallRegular>
            </BodyMediumRegular>
        </YStack>
    );
};

const SyncteraAddressWidget = ({ user }: { user: IdentityMatchUser }) => {
    return (
        <YStack padding={16} alignItems="flex-start">
            <YStack>
                <BodyMediumRegular>{user.address.street}</BodyMediumRegular>
            </YStack>
            <BodyMediumRegular>
                {`${user.address.city}, ${user.address.region} ${user.address.postal_code}`}
            </BodyMediumRegular>
            <BodyMediumRegular>{user.address.country}</BodyMediumRegular>
        </YStack>
    );
};

const extraDataTemplate = (details: UnverifiedExternalAccountResponse) => {
    return (
        <YStack alignItems="flex-start" gap={24}>
            {details.accountIdentity.owners.map((owner, i) => {
                return (
                    <XStack key={`owner${i}`} gap={16} padding={16}>
                        <YStack>
                            <BodySmallRegular>Name</BodySmallRegular>
                            <XStack gap={16} padding={8}>
                                <BodyMediumRegular
                                    backgroundColor="$gray400"
                                    padding={8}
                                >
                                    {`${details.customer.legalFirstName} ${details.customer.legalLastName}`}
                                </BodyMediumRegular>
                                <YStack backgroundColor="$red400" padding={8}>
                                    {owner.names.map((name, i) => {
                                        return (
                                            <BodyMediumRegular key={`name${i}`}>
                                                {name}
                                            </BodyMediumRegular>
                                        );
                                    })}
                                </YStack>
                            </XStack>
                        </YStack>
                        <YStack>
                            <BodySmallRegular>Address</BodySmallRegular>
                            <XStack gap={16} padding={8}>
                                <YStack backgroundColor="$gray400">
                                    <SyncteraAddressWidget
                                        user={details.accountIdentityPayload}
                                    />
                                </YStack>
                                <YStack backgroundColor="$red400">
                                    {owner.addresses.map((address, i) => {
                                        return (
                                            <PlaidAddressWidget
                                                key={`address${i}`}
                                                address={address}
                                            />
                                        );
                                    })}
                                </YStack>
                            </XStack>
                        </YStack>
                        <YStack>
                            <BodySmallRegular>Email</BodySmallRegular>
                            <XStack gap={16} padding={8}>
                                <BodyMediumRegular
                                    padding={8}
                                    backgroundColor="$gray400"
                                >
                                    {details.customer.email}
                                </BodyMediumRegular>
                                <YStack backgroundColor="$red400" padding={8}>
                                    {owner.emails.map(({ data: email }, i) => {
                                        return (
                                            <BodyMediumRegular
                                                key={`email${i}`}
                                            >
                                                {email}
                                            </BodyMediumRegular>
                                        );
                                    })}
                                </YStack>
                            </XStack>
                        </YStack>
                        <YStack>
                            <BodySmallRegular>Phone</BodySmallRegular>
                            <XStack gap={16} padding={8}>
                                <BodyMediumRegular
                                    padding={8}
                                    backgroundColor="$gray400"
                                >
                                    {details.customer.phoneNumber}
                                </BodyMediumRegular>
                                <YStack backgroundColor="$red400" padding={8}>
                                    {owner.phone_numbers.map(
                                        ({ data: phone }, i) => {
                                            return (
                                                <BodyMediumRegular
                                                    key={`phone${i}`}
                                                >
                                                    {phone}
                                                </BodyMediumRegular>
                                            );
                                        },
                                    )}
                                </YStack>
                            </XStack>
                        </YStack>
                    </XStack>
                );
            })}
        </YStack>
    );
};

export const BankOpsExternalAccountReviewPanel = () => {
    const toast = useToastController();
    const [expandedRows, setExpandedRows] = useState<
        DataTableExpandedRows | DataTableValueArray | undefined
    >();

    const [selectedAccounts, setSelectedAccounts] = useState<
        UnverifiedExternalAccountResponse[]
    >([]);

    const [isConfirmModalOpen, setIsConfirmModalOpen] =
        useState<boolean>(false);

    const { data, isLoading, refetch } = useGetUnverifiedExternalAccounts({});

    useEffect(() => {
        setSelectedAccounts([]);
    }, [data]);

    const { mutateAsync: updateExternalAccount, isPending } =
        useUpdateExternalAccount({
            onSettled: async () => {
                await refetch();
            },
        });

    const handleApprove = async (): Promise<void> => {
        await updateExternalAccount(
            {
                body: {
                    manualReviewStatus: 'MANUAL_APPROVED',
                    ids: selectedAccounts.map((a) => a.id),
                },
            },
            {
                onSuccess: () => {
                    toast.show(
                        'External account(s) approved',
                        MutationSuccessToastOptions,
                    );
                },
                onError: () => {
                    toast.show(
                        'Could not update external accounts',
                        MutationErrorToastOptions(),
                    );
                },
                onSettled: () => {
                    setSelectedAccounts([]);
                },
            },
        );
    };

    const handleReject = async () => {
        await updateExternalAccount(
            {
                body: {
                    manualReviewStatus: 'REJECTED',
                    ids: selectedAccounts.map((a) => a.id),
                },
            },
            {
                onSuccess: () => {
                    toast.show(
                        'External account(s) rejected',
                        MutationSuccessToastOptions,
                    );
                },
                onError: () => {
                    toast.show(
                        'Could not update external accounts',
                        MutationErrorToastOptions(),
                    );
                },
                onSettled: () => {
                    setSelectedAccounts([]);
                },
            },
        );
    };

    const handleInReview = async (): Promise<void> => {
        await updateExternalAccount(
            {
                body: {
                    manualReviewStatus: 'IN_REVIEW',
                    ids: selectedAccounts.map((a) => a.id),
                },
            },
            {
                onSuccess: () => {
                    toast.show(
                        'External account(s) in review',
                        MutationSuccessToastOptions,
                    );
                },
                onError: () => {
                    toast.show(
                        'Could not update external accounts',
                        MutationErrorToastOptions(),
                    );
                },
                onSettled: () => {
                    setSelectedAccounts([]);
                },
            },
        );
    };

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    const externalAccounts = data ?? [];

    return (
        <YStack gap={16}>
            <H6Emphasized>
                Unverified External Accounts{' '}
                {externalAccounts.length > 0
                    ? `(${externalAccounts.length})`
                    : ''}
            </H6Emphasized>
            {externalAccounts.length === 0 && (
                <BodyMediumRegular padding={24}>
                    No accounts found
                </BodyMediumRegular>
            )}

            <DataTable<UnverifiedExternalAccountResponse[]>
                dataKey="id"
                sortField="customer.email"
                sortOrder={1}
                groupRowsBy="customer.email"
                rowGroupMode="subheader"
                rowGroupHeaderTemplate={(
                    data: UnverifiedExternalAccountResponse,
                ) => (
                    <BodyLargeEmphasized>
                        {data.customer.legalFirstName}{' '}
                        {data.customer.legalLastName}
                    </BodyLargeEmphasized>
                )}
                selection={selectedAccounts}
                selectionMode="checkbox"
                showSelectAll={false}
                onRowSelect={(e: DataTableSelectEvent) => {
                    const account = e.data as UnverifiedExternalAccountResponse;
                    setSelectedAccounts((prev) => {
                        return [...prev, account];
                    });
                }}
                onRowUnselect={(e: DataTableSelectEvent) => {
                    const account = e.data as UnverifiedExternalAccountResponse;
                    setSelectedAccounts((prev) => {
                        return prev.filter((a) => a.id !== account.id);
                    });
                }}
                value={externalAccounts}
                expandedRows={expandedRows}
                rowExpansionTemplate={extraDataTemplate}
                onRowToggle={(e) => {
                    setExpandedRows(e.data);
                }}
            >
                <Column
                    selectionMode="multiple"
                    headerStyle={{ width: '3rem' }}
                ></Column>
                <Column expander />
                <Column
                    header="Match score"
                    field="accountIdentityMatchScore.legal_name.score"
                />
                <Column
                    header="Name"
                    body={({ customer }: UnverifiedExternalAccountResponse) => {
                        return `${customer.legalFirstName} ${customer.legalLastName}`;
                    }}
                    hidden={true}
                />
                <Column header="Email" field="customer.email" />
                <Column header="Institution" field="institutionName" />
                <Column header="Account name" field="name" />
                <Column header="Created at" field="createdAt" />
                <Column
                    header="Status"
                    body={(account: UnverifiedExternalAccountResponse) => {
                        let variant: BadgeLabelVariant = 'neutral';
                        if (account.manualReviewStatus === 'NEEDS_REVIEW') {
                            variant = 'positive';
                        }
                        if (account.manualReviewStatus === 'IN_REVIEW') {
                            variant = 'blue';
                        }
                        return (
                            <BadgeLabel
                                variant={variant}
                                size="large"
                                text={transformEnumToDisplay(
                                    account.manualReviewStatus ?? '',
                                )}
                            />
                        );
                    }}
                />
            </DataTable>
            <YStack alignItems="flex-start">
                <XStack paddingTop={4} gap={8} alignItems="flex-start">
                    <Button
                        text="Approve"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.TINY}
                        buttonProps={{
                            backgroundColor:
                                selectedAccounts.length === 0
                                    ? '$gray300'
                                    : '$green1000',
                        }}
                        disabled={selectedAccounts.length === 0 || isPending}
                        onPress={() => {
                            void handleApprove();
                        }}
                    />
                    <Button
                        text="Reject"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.TINY}
                        buttonProps={{
                            backgroundColor:
                                selectedAccounts.length === 0
                                    ? '$gray300'
                                    : '$red1000',
                        }}
                        disabled={selectedAccounts.length === 0 || isPending}
                        onPress={() => {
                            setIsConfirmModalOpen(true);
                        }}
                    />
                    <Button
                        text="In Review"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.TINY}
                        buttonProps={{
                            backgroundColor:
                                selectedAccounts.length === 0
                                    ? '$gray300'
                                    : '$orange1000',
                        }}
                        disabled={selectedAccounts.length === 0 || isPending}
                        onPress={() => {
                            void handleInReview();
                        }}
                    />
                </XStack>
            </YStack>
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                }}
                onConfirm={() => {
                    void handleReject();
                    setIsConfirmModalOpen(false);
                }}
                description="Reject selected account(s) - Are you sure?"
            />
        </YStack>
    );
};
